import React from "react";
import { View, Text, StyleSheet, Document, Page } from "@react-pdf/renderer";
import type { Medication } from "../types/medication";
import StandardPDFLayout from "./StandardPDFLayout";

interface PrescriptionPDFProps {
  medications: Medication[];
  patientInfo: {
    nom: string;
    prenom: string;
    dateNaissance: string;
    adresse?: string;
  };
  doctorInfo: {
    firstName: string;
    lastName: string;
    title: string;
    specialty?: string; // ✅ Ajouter la spécialité du médecin
  };
  consultationDate: string;
  orderNumber?: string;
  pageSize?: "A4" | "A5";
  prescriptionText?: string;
  instructions?: string;
}

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 9,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },
  patientSection: {
    backgroundColor: "#f8f9ff",
    padding: 6,
    marginBottom: 6,
    borderRadius: 3,
    borderLeftWidth: 2,
    borderLeftColor: "#113285",
  },
  patientTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#113285",
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  patientGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  patientItem: {
    width: "48%",
    marginBottom: 2,
  },
  patientLabel: {
    fontSize: 7,
    color: "#4b5563",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },
  patientValue: {
    fontSize: 8,
    color: "#111827",
    fontWeight: "normal",
    marginTop: 1,
  },
  medicationsSection: {
    marginBottom: 6,
  },
  medicationsTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#113285",
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    paddingBottom: 2,
  },
  medicationsList: {
    paddingLeft: 0,
  },
  medicationItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 2,
    paddingVertical: 1,
    borderBottomWidth: 0.5,
    borderBottomColor: "#f0f0f0",
    paddingBottom: 2,
  },
  medicationNumber: {
    width: 15,
    fontSize: 8,
    fontWeight: "bold",
    color: "#113285",
    textAlign: "right",
    marginRight: 5,
  },
  medicationContent: {
    flex: 1,
    flexDirection: "column",
  },
  medicationName: {
    fontSize: 8,
    color: "#111827",
    fontWeight: "bold",
    marginBottom: 1,
    lineHeight: 1.1,
  },
  medicationDetails: {
    fontSize: 7,
    color: "#4b5563",
    lineHeight: 1.0,
  },
  signatureSection: {
    marginTop: 15,
    paddingTop: 10,
    borderTop: "1px solid #e5e7eb",
    width: "100%",
  },
  signatureContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 10,
    width: "100%",
  },
  signatureBox: {
    width: "48%",
    textAlign: "center",
  },
  signatureLine: {
    borderBottom: "1px dashed #000000",
    height: 30,
    marginBottom: 3,
    width: "100%",
  },
  stampBox: {
    width: "48%",
    textAlign: "center",
  },
  stampArea: {
    height: 30,
    border: "1px dashed #9ca3af",
    borderRadius: 2,
    marginBottom: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  stampText: {
    fontSize: 7,
    color: "#6b7280",
  },
  signatureLabel: {
    fontSize: 7,
    color: "#4a5568",
    marginTop: 2,
  },
  doctorInfoSection: {
    marginTop: 10,
    textAlign: "center",
  },
  doctorName: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#113285",
    marginBottom: 1,
  },
  doctorSpecialty: {
    fontSize: 7,
    color: "#4b5563",
    marginBottom: 2,
  },
  signatureDate: {
    fontSize: 7,
    color: "#6b7280",
    marginTop: 2,
  },
});

const formatDate = (dateString: string | undefined | null) => {
  if (!dateString || dateString.trim() === "") {
    return "Non spécifiée";
  }

  const dateParts = dateString.split("-");

  let date: Date;

  if (dateParts.length === 3) {
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1;
    const day = parseInt(dateParts[2]);

    date = new Date(year, month, day);
  } else {
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) {
    return "Date invalide";
  }

  const formattedDate = date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return formattedDate;
};

const formatMedicationDetails = (med: Medication) => {
  const parts = [];

  if (med.dose && med.doseUnit) {
    parts.push(`${med.dose} ${med.doseUnit}`);
  }

  if (med.frequency) {
    parts.push(med.frequency);
  }

  if (med.duration && med.durationUnit) {
    parts.push(`Pendant ${med.duration} ${med.durationUnit}`);
  }

  return parts.join(" • ");
};

const PrescriptionContent: React.FC<PrescriptionPDFProps> = ({
  medications,
  patientInfo,
  doctorInfo,
  consultationDate,
  orderNumber,
  pageSize = "A5",
  prescriptionText,
}) => {
  const currentDate = new Date().toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const patientFullName =
    `${patientInfo.prenom} ${patientInfo.nom}`.toUpperCase();
  const getPatientAge = (dob: string | undefined | null) => {
    if (!dob) return "Non spécifié";
    
    // Gérer différents formats de date
    let birthDate: Date;
    
    // Format ISO: YYYY-MM-DD
    if (dob.includes('-')) {
      const dateParts = dob.split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1;
        const day = parseInt(dateParts[2]);
        birthDate = new Date(year, month, day);
      } else {
        birthDate = new Date(dob);
      }
    } else {
      // Autres formats
      birthDate = new Date(dob);
    }
    
    if (isNaN(birthDate.getTime())) return "Date invalide";
    
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age + " ans";
  };

  const patientAge = getPatientAge(patientInfo.dateNaissance);

  return (
    <StandardPDFLayout
      title="ORDONNANCE MÉDICALE"
      documentNumber={orderNumber}
      documentDate={consultationDate}
      pageSize={pageSize}
    >
      {/* Patient Information Section */}
      <View style={styles.patientSection}>
        <Text style={styles.patientTitle}>Informations du patient</Text>
        <View style={styles.patientGrid}>
          <View style={styles.patientItem}>
            <Text style={styles.patientLabel}>Nom et prénom</Text>
            <Text style={styles.patientValue}>{patientFullName}</Text>
          </View>
          <View style={styles.patientItem}>
            <Text style={styles.patientLabel}>Date de naissance</Text>
            <Text style={styles.patientValue}>
              {formatDate(patientInfo.dateNaissance)} ({patientAge})
            </Text>
          </View>
          {patientInfo.adresse && (
            <View style={[styles.patientItem, { width: "100%" }]}>
              <Text style={styles.patientLabel}>Adresse</Text>
              <Text style={styles.patientValue}>{patientInfo.adresse}</Text>
            </View>
          )}
        </View>
      </View>

      {/* Medications Section */}
      <View style={styles.medicationsSection}>
        <Text style={styles.medicationsTitle}>Médicaments prescrits</Text>
        <View style={styles.medicationsList}>
          {prescriptionText ? (
            <View style={styles.medicationItem}>
              <Text style={styles.medicationNumber}>1.</Text>
              <View style={styles.medicationContent}>
                <Text style={{ fontSize: 9, color: "#111827", lineHeight: 1.4 }}>
                  {prescriptionText}
                </Text>
              </View>
            </View>
          ) : (
            medications.map((med, index) => (
              <View key={index} style={styles.medicationItem}>
                <Text style={styles.medicationNumber}>{index + 1}.</Text>
                <View style={styles.medicationContent}>
                  <Text style={styles.medicationName}>
                    {med.name}{" "}
                    {med.quantity ? `- ${med.quantity} ${med.quantityUnit}` : ""}
                  </Text>
                  <Text style={styles.medicationDetails}>
                    {med.instructions ||
                      formatMedicationDetails(med) ||
                      "Posologie non spécifiée"}
                  </Text>
                  {(med.advice || med.startDate || med.endDate) && (
                    <Text style={{ fontSize: 6, color: "#6b7280", fontStyle: "italic", marginTop: 1 }}>
                      {med.advice && `Conseils: ${med.advice}`}
                      {med.advice && (med.startDate || med.endDate) && " - "}
                      {med.startDate && `Début: ${formatDate(med.startDate)}`}
                      {med.startDate && med.endDate && " - "}
                      {med.endDate && `Fin: ${formatDate(med.endDate)}`}
                    </Text>
                  )}
                </View>
              </View>
            ))
          )}
        </View>
      </View>

      <View style={{ marginTop: 20, flexDirection: 'row', justifyContent: 'flex-end' }}>
        <View style={{ width: '50%', alignItems: 'flex-end' }}>
          {/* Zone de signature et cachet */}
          <View style={{ marginBottom: 25, width: '100%' }}>
            <Text style={{ fontSize: 6, color: "#4a5568", textAlign: 'right', marginBottom: 3 }}>Signature et cachet du médecin</Text>
          </View>

          {/* Informations du médecin */}
          <View style={{ textAlign: 'right', width: '100%' }}>
            <Text style={{ fontSize: 7, fontWeight: "bold", color: "#113285", marginBottom: 1, textAlign: 'right' }}>
              {doctorInfo.title} {doctorInfo.firstName}{" "}
              {doctorInfo.lastName.toUpperCase()}
            </Text>
            <Text style={{ fontSize: 6, color: "#4b5563", marginBottom: 1, textAlign: 'right' }}>
              {doctorInfo.specialty || "Médecin Généraliste"}
            </Text>
            <Text style={{ fontSize: 6, color: "#6b7280", marginTop: 1, textAlign: 'right' }}>Fait le {currentDate}</Text>
          </View>
        </View>
      </View>
    </StandardPDFLayout>
  );
};

const PrescriptionPDF: React.FC<PrescriptionPDFProps> = (props) => (
  <Document>
    <Page size={props.pageSize ?? "A5"} style={styles.page}>
      <PrescriptionContent {...props} />
    </Page>
  </Document>
);

export default PrescriptionPDF;
